import React, { useEffect, useState } from 'react';
import './EnglishBlog.css';
import { apiGet } from '../api';

const EnglishBlog = () => {
  const [remotePosts, setRemotePosts] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        setLoading(true);
        // Fetch non-ad posts and ads in parallel, then combine to ensure up to 6 unique items
        const [postsResp, adsResp] = await Promise.all([
          apiGet('/api/posts', { locale: 'en', isAd: 'false', limit: 50 }),
          apiGet('/api/posts', { locale: 'en', isAd: true, limit: 50 }),
        ]);

        const postsArr = Array.isArray(postsResp?.items) ? postsResp.items : [];
        const adsArr = Array.isArray(adsResp?.items) ? adsResp.items : [];

        const mapItem = (p) => ({
          id: p.id,
          title: p.title,
          excerpt: p.excerpt || '',
          date: p.date,
          category: p.categoryLabel || (p.category && (p.category.name || p.category.slug)) || 'post',
          image: p.imageUrl || undefined,
          type: p.isAd ? 'ad' : 'post',
          adText: p.adText || undefined,
          ctaText: p.ctaText || undefined,
          slug: p.slug || undefined,
        });

        const combined = [...postsArr.map(mapItem), ...adsArr.map(mapItem)].filter((m) => m.title);
        // Stronger de-duplication: by slug, id, normalized title, and image URL
        const seenSlugs = new Set();
        const seenIds = new Set();
        const seenTitles = new Set();
        const seenImages = new Set();
        const unique = [];
        for (const item of combined) {
          const slugKey = (item.slug || '').toString().trim().toLowerCase();
          const idKey = (item.id || '').toString();
          const titleKey = (item.title || '').toString().trim().toLowerCase();
          const imageKey = (item.image || '').toString().trim().toLowerCase();

          if (slugKey && seenSlugs.has(slugKey)) continue;
          if (idKey && seenIds.has(idKey)) continue;
          if (titleKey && seenTitles.has(titleKey)) continue;
          if (imageKey && seenImages.has(imageKey)) continue;

          if (slugKey) seenSlugs.add(slugKey);
          if (idKey) seenIds.add(idKey);
          if (titleKey) seenTitles.add(titleKey);
          if (imageKey) seenImages.add(imageKey);

          unique.push(item);
          if (unique.length >= 6) break;
        }

        if (!ignore) {
          setRemotePosts(unique);
        }
      } catch (_) {}
      finally { setLoading(false); }
    })();
    return () => { ignore = true; };
  }, []);
  const content = {
    title: 'Purayidakrishi - Regenerative Homestead Agroecology',
    subtitle: 'This blog focuses on Regenerative Homestead Agroecology and Organic Farming, highlighting practices that promote biodiversity, soil health, and seed conservation, along with farming-related issues and policies that support climate-resilient farming systems.',
    readMore: 'Read More'
  };

  // Ensure we always show 6 posts by repeating posts if needed
  let postsToShow = remotePosts.slice(0, 6);
  
  // If we have fewer than 6 posts, repeat the existing posts to fill the grid
  if (postsToShow.length > 0 && postsToShow.length < 6) {
    while (postsToShow.length < 6) {
      const remainingSlots = 6 - postsToShow.length;
      const postsToAdd = remotePosts.slice(0, Math.min(remainingSlots, remotePosts.length));
      postsToShow = [...postsToShow, ...postsToAdd.map((post, index) => ({
        ...post,
        id: `${post.id}-repeat-${index}` // Ensure unique keys
      }))];
    }
  }
  
  return (
    <section id="english-blog" className="english-blog-section">
      <div className="container">
        <div className="english-blog-header">
          <h2 className="english-blog-title">{content.title}</h2>
          <p className="english-blog-subtitle">{content.subtitle}</p>
        </div>

        <div className="english-blog-grid">
          {postsToShow.map((post) => (
            <article key={post.id} className={`english-blog-card ${post.type === 'ad' ? 'english-blog-card-ad' : ''}`}>
              <div className="english-blog-image">
                <img 
                  src={post.image} 
                  alt={post.title}
                  onError={(e) => {
                    e.target.src = `https://via.placeholder.com/400x250/2d5016/ffffff?text=${encodeURIComponent(post.category)}`;
                  }}
                />
                <div className={`english-blog-category ${post.type === 'ad' ? 'english-blog-category-ad' : ''}`}>
                  {post.type === 'ad' && post.adText && (
                    <span className="english-ad-label">{post.adText}</span>
                  )}
                  {post.category}
                </div>
              </div>
              
              <div className="english-blog-content">
                {post.type === 'post' && (
                  <div className="english-blog-meta">
                    <span className="english-blog-date">{post.date}</span>
                  </div>
                )}
                
                <h3 className="english-blog-post-title">{post.title}</h3>
                <p className="english-blog-excerpt">{post.excerpt}</p>
                
                <button
                  className={`english-blog-read-more ${post.type === 'ad' ? 'english-blog-cta-button' : ''}`}
                  onClick={() => {
                    if (post.type === 'post' && post.slug) {
                      window.scrollTo(0, 0);
                      window.location.hash = `#/blog/en/${post.slug}`;
                    }
                  }}
                >
                  {post.type === 'ad' ? post.ctaText : content.readMore}
                  <svg className="english-read-more-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </article>
          ))}
        </div>

        <div className="english-blog-footer">
          <button className="english-view-all-posts" onClick={() => { window.scrollTo(0, 0); window.location.hash = '#/blog/en'; }}>
            View All English Posts
          </button>
          {postsToShow.length === 0 && !loading && (
            <div style={{ textAlign: 'center', color: '#6b7280', marginTop: 8 }}>No posts found.</div>
          )}
        </div>
      </div>
    </section>
  );
};

export default EnglishBlog;
